import { cn } from './cn';
import { type SegmentedSize, segmentedItem, segmentedTrack } from './segmented';

/**
 * A row of mutually exclusive options — pick one of a few.
 *
 * The same markup had been copied into several trigger editors, each
 * re-deciding the selected and unselected styling, so the variants had started
 * to drift. Rendered as a radiogroup so the choice is announced as a choice and
 * arrow keys behave the way users expect from a segmented control.
 *
 * It looks the way it does for the reasons in `segmented.ts`. What it used to
 * be was a row of separate bordered buttons with a `gap-2` between them — not
 * a segmented control at all, but three controls that happened to be near each
 * other — with the chosen one filled in the brand. Stretched across the Alerts
 * page that filled option was the largest block of colour in the product.
 */
export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  disabled?: boolean;
}

interface SegmentedControlProps<T extends string> {
  options: ReadonlyArray<SegmentedOption<T>>;
  /** `undefined` means nothing is chosen yet — a legitimate initial state. */
  value: T | undefined;
  onChange: (value: T) => void;
  /**
   * Stretch the options to fill the row on a phone, where a pill track that
   * hugs its labels leaves a ragged gap; from `sm` up it always hugs them,
   * because a three-option track spread across a wide page reads as a bar.
   */
  stretch?: boolean;
  size?: SegmentedSize;
  'aria-label'?: string;
  className?: string;
}

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  stretch = true,
  size = 'md',
  className,
  ...rest
}: SegmentedControlProps<T>) {
  return (
    <div
      role="radiogroup"
      aria-label={rest['aria-label']}
      className={segmentedTrack(
        size,
        cn(stretch && 'flex w-full sm:inline-flex sm:w-auto', className)
      )}
    >
      {options.map((option) => {
        const selected = option.value === value;
        return (
          // biome-ignore lint/a11y/useSemanticElements: a styled segmented control, not a list of radio inputs
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={option.disabled}
            onClick={() => onChange(option.value)}
            className={segmentedItem(selected, size, cn(stretch && 'flex-1 sm:flex-none'))}
            title={option.label}
          >
            {/* The label truncates, not the button. `truncate` on a
                `justify-center` flex container clips the text at both ends and
                never shows the ellipsis — in Russian the Reports tab rendered
                as "аннёные и по распис", the middle of its own word. An inner
                span is what the ellipsis needs to be the end of. */}
            <span className="truncate">{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}
